/** Strings owned by `src/features/overview/` — WP-06's `/overview`
 * company-dashboard, Home's first subitem. */
export const overview = {
  page: {
    title: 'Overview',
    lead: 'How is {{company}} doing?',
    noScope: 'Select a company to see its overview.',
    noDepartments: 'No department here has statistics you may see.',
    disclaimer:
      'All figures are indicative group-level well-being insights and are not intended as a medical diagnosis.',
  },
  summary: {
    caption: 'AI summary based on anonymized, aggregated data.',
  },
  figures: {
    vitality: 'Vitality',
    stress: 'Stress',
    sleep: 'Sleep',
    engagement: 'Engagement',
    activeParticipants: 'Active participants',
  },
  figuresInfo: {
    vitality: 'A composite score based on sleep, activity, HRV, stress and heart rate.',
    stress: 'Average stress level, measured directly by the wearable.',
    sleep: 'Average sleep duration per night, measured directly by the wearable.',
    engagement: 'Share of employees actively using the app.',
    activeParticipants: 'Number of employees active over the past period.',
  },
  legend: {
    title: 'What you are seeing',
    measured:
      'Directly measured — straight from the NOLATR wearable or a connected sensor. Highest reliability.',
    aiDerived:
      'AI-derived — not a direct measurement. The AI combines several signals into one indicator.',
    usage: 'Usage & behavior — actual interactions in the app, not a health measurement.',
  },
  developments: {
    title: 'Key developments',
    empty: 'No notable developments this period.',
    stress: {
      title: 'Opportunity to offer rest (stress elevated)',
      body: 'A friendly rest moment or breathing tip can help the team recharge.',
    },
    sleep: {
      title: 'Opportunity: sleep below target',
      body: 'A targeted, positive recovery tip can support the team well.',
    },
    vitalityGood: {
      title: 'Vitality is trending well',
      body: 'The team is holding its current rhythm — a good moment to acknowledge that.',
    },
    needsReactivation: {
      title: '{{count}} people need reactivation',
      body: 'A short, personal reactivation message usually works best.',
    },
    mostActiveWeekday: {
      title: 'Most energy on {{weekday}}',
      body: 'Plan challenges and communications around the most active day for maximum reach.',
    },
    challengeParticipation: {
      title: 'Challenge participation {{pct}}%',
      body: 'A new, low-threshold team challenge could get more people involved.',
    },
  },
  actions: {
    title: 'Actions for HR',
    empty: 'No recommended actions right now.',
    sendReminder: {
      title: '{{count}} employees not yet activated',
      subtitle: 'Send a reminder',
    },
    reactivate: {
      title: '{{count}} employees need reactivation',
      subtitle: 'Reach out',
    },
    stressCheckIn: {
      title: 'Elevated stress across the team',
      subtitle: 'Consider a short workload check-in',
    },
    sleepReminder: {
      title: 'Sleep below target across the team',
      subtitle: 'Consider a reminder about wind-down routines and screen time',
    },
    activeChallenge: {
      title: "'{{name}}' is running — plan the next one",
      subtitle: 'Challenges',
    },
    pilotEvaluation: {
      interim: {
        title: "Pilot '{{name}}' is ready for its interim evaluation",
        subtitle: 'View pilot',
      },
      final: {
        title: "Pilot '{{name}}' is ready for its final evaluation",
        subtitle: 'View pilot',
      },
    },
  },
  energy: {
    title: 'Energy & wellbeing',
    subtitle:
      'How the team experiences the workday — energy, breaks, work rhythm and engagement. Aggregated only, never individual.',
    badge: 'Aggregated & anonymized',
    tbd: 'To be determined',
    missingData: {
      noSource: 'Energy index and eNPS: no data source decided yet.',
      insufficientParticipants: 'Not enough participants for {{metrics}}.',
    },
    privacyNote:
      'Supportive insights, not monitoring. Indicative wellbeing at group level — no medical interpretation.',
    metrics: {
      energyIndex: 'Energy index',
      breakAdoption: 'Break adoption',
      focusRhythm: 'Focus rhythm',
      coachAdoption: 'Coach adoption',
      enps: 'eNPS',
    },
  },
  pilots: {
    title: 'Pilot status',
    empty: 'No pilots yet.',
    participantsAndDay: '{{count}} participants · day {{day}} of {{total}}',
    viewReport: 'View pilot report',
  },
  challenges: {
    title: 'Active challenges',
    empty: 'No challenges yet.',
    active: 'Active',
    total: 'Total',
    participants: 'Participants',
    activeBadge: '{{count}} active',
    manageLink: 'Manage challenges',
    noneActive: 'No active challenges right now.',
    participantsCount: '{{count}} participants',
  },
  trends: {
    title: 'Trends',
    filters: {
      allDepartments: 'All departments',
      participants: {
        label: 'Participants',
        all: 'All participants',
        manager: 'Managers',
        employee: 'Employees',
      },
    },
    vitalityScore: 'Vitality score over time',
    activeUsersPerWeek: 'Active users per week',
    sleep: 'Sleep trend',
    stress: 'Stress trend',
    hrv: 'HRV trend',
    recovery: 'Recovery trend',
    activity: 'Activity trend',
    notAvailable: 'No data source for this trend yet — not available in this release.',
    vsPreviousPeriod: 'vs previous period',
    info: {
      vitalityScore: 'A composite vitality score per day, based on all vitality signals.',
      activeUsersPerWeek: 'Number of unique employees who actively used the app each week.',
      sleep: 'Average sleep duration per day, measured directly by the wearable.',
      stress: 'Average stress level per day, measured directly by the wearable.',
      hrv: 'Average heart rate variability (HRV) per day, an indicator of recovery.',
      recovery: 'A composite recovery score per day, based on sleep, HRV and activity.',
      activity: 'Average step count per day, measured directly by the wearable.',
    },
  },
} as const;
